'use client'
import React, { forwardRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from './TextField';

/**
 * Enhanced TextField component with debouncing, maxLength validation, and automatic placeholder generation.
 *
 * This component improves form performance by delaying state updates until the user stops typing,
 * while maintaining all the features of the original AltTextField component.
 *
 * DESIGN DECISION: React 18 Style with forwardRef
 * ===============================================
 * This component uses React.forwardRef for ref forwarding, following React 18 patterns.
 * This approach ensures:
 * - Compatibility with React 18 and earlier versions
 * - Consistent API with other components in the codebase
 * - Explicit ref handling that's easy to understand and debug
 * - Future migration path to React 19 when ready
 *
 * REF FORWARDING BEHAVIOR:
 * The ref is forwarded directly to the underlying TextField component, which then
 * forwards it to the actual input DOM element. This allows parent components to:
 * - Focus the input programmatically: inputRef.current?.focus()
 * - Access input properties: inputRef.current?.value
 * - Trigger input methods: inputRef.current?.select()
 * - Integrate with form libraries: React Hook Form, Formik, etc.
 *
 * @param {number} maxLength - Maximum number of characters allowed (0 = no limit)
 * @param {number} debounceDelay - Delay in milliseconds before triggering onChange (0 = disabled)
 * @param {string} value - Controlled input value from parent component
 * @param {function} onChange - Callback function when value changes (receives synthetic event)
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @param {object} props - All other TextField props (placeholder, inputProps, etc.)
 *
 * @example
 * // Basic usage with debouncing and ref access
 * const inputRef = useRef(null);
 *
 * <DebouncedTextField
 *   ref={inputRef}
 *   label="Entry Number"
 *   value={formData.entryNumber}
 *   onChange={handleEntryNumberChange}
 *   maxLength={11}
 *   debounceDelay={300}
 * />
 *
 * // Later: inputRef.current?.focus()
 *
 * @example
 * // For calculation-heavy fields, use longer delay
 * <DebouncedTextField
 *   label="Total Value"
 *   type="number"
 *   value={item.enteringValue}
 *   onChange={handleValueChange}
 *   debounceDelay={500}
 * />
 *
 * @example
 * // Form validation with programmatic focus
 * const handleSubmit = () => {
 *   if (!email) {
 *     emailRef.current?.focus();
 *     return;
 *   }
 *   // Submit form...
 * };
 *
 * @example
 * // Integration with React Hook Form
 * <DebouncedTextField
 *   {...register('username')}
 *   label="Username"
 *   debounceDelay={300}
 * />
 */
/**
 * REACT 18 STYLE COMPONENT WITH EXPLICIT REF FORWARDING
 * =====================================================
 *
 * This component uses React.forwardRef to explicitly handle ref forwarding.
 * This pattern ensures maximum compatibility and clear intent.
 *
 * COMPONENT SIGNATURE:
 * - First parameter: props object (destructured)
 * - Second parameter: ref (forwarded from parent)
 *
 * The forwardRef wrapper allows parent components to access the underlying
 * input DOM element through the ref, enabling programmatic control.
 */
const DebouncedTextField = forwardRef(
  (
    {
      maxLength = 0,
      debounceDelay = 500, // Default 500ms provides good balance between performance and UX
      value = '',
      onChange,
      ...props
    },
    ref,
  ) => {
    // Extract placeholder and inputProps from props to avoid passing them twice
    const { placeholder: parentPlaceholder, inputProps: parentInputProps = {}, ...rest } = props;

    // Local state stores the immediate input value for responsive UI
    // This allows instant visual feedback while delaying the actual state update
    const [localValue, setLocalValue] = useState(value);

    // Auto-generate helpful placeholder text based on maxLength constraint
    // Priority: explicit placeholder > auto-generated > empty string
    const placeholder =
      parentPlaceholder ||
      (maxLength > 0 ? `Maximum of ${maxLength} character${maxLength > 1 ? 's' : ''}` : '');

    // Combine maxLength validation with any existing inputProps
    // This ensures maxLength is enforced at the HTML input level for immediate feedback
    const mergedInputProps = {
      maxLength: maxLength > 0 ? maxLength : undefined, // undefined removes the attribute entirely
      ...parentInputProps, // Parent inputProps can override maxLength if needed
    };

    // SYNC EFFECT: Keep local state in sync when external value changes
    // This handles cases where the parent component updates the value programmatically
    // (e.g., form reset, load from API, validation corrections)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // DEBOUNCE EFFECT: Delay onChange calls until user stops typing
    // This is the core performance optimization that prevents excessive re-renders
    useEffect(() => {
      // Only set up debouncing if delay > 0 and onChange is provided
      if (debounceDelay > 0 && onChange) {
        const timeoutId = setTimeout(() => {
          // Only call onChange if the local value actually differs from the external value
          // This prevents unnecessary state updates and potential infinite loops
          if (localValue !== value) {
            // Create synthetic event that matches TextField's onChange signature
            // This ensures compatibility with existing form handlers that expect event.target.value
            const syntheticEvent = {
              target: {
                value: localValue,
                name: rest.name || '', // Include name for form libraries that use it
              },
            };
            onChange(syntheticEvent);
          }
        }, debounceDelay);

        // Cleanup: Cancel the timeout if component unmounts or dependencies change
        // This prevents memory leaks and stale closures
        return () => clearTimeout(timeoutId);
      }
      // Dependencies: Re-run when any of these values change
    }, [localValue, debounceDelay, onChange, value, rest.name]);

    // INPUT CHANGE HANDLER: Update local state immediately for responsive UI
    const handleInputChange = useCallback(
      (e) => {
        const newValue = e.target.value;

        // Always update local state immediately for responsive visual feedback
        setLocalValue(newValue);

        // BYPASS DEBOUNCING: If debouncing is disabled, call onChange immediately
        // This maintains backward compatibility with non-debounced usage
        if (debounceDelay === 0 && onChange) {
          onChange(e); // Pass through the original event object
        }
      },
      [debounceDelay, onChange], // Re-create only when these dependencies change
    );

    // COMPONENT RENDER: Forward ref to underlying TextField
    // The ref parameter from forwardRef is passed directly to TextField,
    // which then forwards it to the actual input DOM element
    return (
      <TextField
        fullWidth // Default to full width for consistent form layouts
        placeholder={placeholder} // Use computed placeholder with maxLength logic
        inputProps={mergedInputProps} // Apply maxLength constraint and user inputProps
        value={localValue} // Use local state for immediate visual feedback
        onChange={handleInputChange} // Handle input with debouncing logic
        ref={ref} // CRITICAL: Forward the ref to enable programmatic access
        {...rest} // Spread remaining props (label, required, type, etc.)
      />
    );
  },
);

// Comprehensive prop validation for development safety and documentation
// Note: 'ref' prop is handled by React.forwardRef and doesn't need PropTypes validation
DebouncedTextField.propTypes = {
  // DEBOUNCE & VALIDATION PROPS
  maxLength: PropTypes.number /** Maximum number of characters allowed (0 = no limit) */,
  debounceDelay:
    PropTypes.number /** Delay in milliseconds before triggering onChange (0 = disabled, default: 500) */,

  // CORE INPUT PROPS
  value: PropTypes.string /** Controlled input value from parent component */,
  onChange: PropTypes.func /** Callback function when value changes after debounce delay */,
  name: PropTypes.string /** Input name attribute for form handling */,
  placeholder:
    PropTypes.string /** Placeholder text (auto-generated from maxLength if not provided) */,

  // STYLING & LAYOUT PROPS
  fullWidth: PropTypes.bool /** Make input take full width of container (default: true) */,
  size: PropTypes.oneOf(['small', 'medium']) /** Input size variant */,
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']) /** Input style variant */,
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
  ]) /** Theme color for focus states */,

  // CONTENT & LABELING PROPS
  label: PropTypes.string /** Field label text */,
  helperText: PropTypes.string /** Explanatory text below the input */,

  // STATE & VALIDATION PROPS
  required: PropTypes.bool /** Mark field as required for form validation */,
  disabled: PropTypes.bool /** Disable all user interactions */,
  error: PropTypes.bool /** Apply error styling (red colors) */,
  autoFocus: PropTypes.bool /** Automatically focus input on mount */,

  // INPUT TYPE & BEHAVIOR PROPS
  type: PropTypes.string /** HTML input type (text, number, email, password, etc.) */,
  multiline: PropTypes.bool /** Enable multi-line textarea mode */,
  rows: PropTypes.number /** Number of rows for multiline input */,
  maxRows: PropTypes.number /** Maximum number of rows for multiline input */,
  minRows: PropTypes.number /** Minimum number of rows for multiline input */,

  // ADVANCED CUSTOMIZATION PROPS
  inputProps: PropTypes.object /** Props passed directly to the input element */,
  InputProps: PropTypes.object /** Props passed to the Input component */,
  InputLabelProps: PropTypes.object /** Props passed to the InputLabel component */,
  FormHelperTextProps: PropTypes.object /** Props passed to the FormHelperText component */,

  // EVENT HANDLERS (beyond onChange)
  onBlur: PropTypes.func /** Callback fired when input loses focus */,
  onFocus: PropTypes.func /** Callback fired when input gains focus */,
  onKeyDown: PropTypes.func /** Callback fired on key press */,
  onKeyUp: PropTypes.func /** Callback fired on key release */,

  // ACCESSIBILITY PROPS
  id: PropTypes.string /** Unique identifier for the input */,
  'aria-label': PropTypes.string /** Accessibility label when no visible label */,
  'aria-labelledby': PropTypes.string /** ID of element that labels this input */,
  'aria-describedby': PropTypes.string /** ID of element that describes this input */,

  // REF HANDLING NOTE:
  // The 'ref' prop is automatically handled by React.forwardRef and doesn't require
  // PropTypes validation. It's passed as the second parameter to the component function.
};

// Default prop values for optional props
DebouncedTextField.defaultProps = {
  maxLength: 0,
  debounceDelay: 500,
  value: '',
  fullWidth: true,
  size: 'medium',
  variant: 'outlined',
  color: 'primary',
  type: 'text',
  required: false,
  disabled: false,
  error: false,
  autoFocus: false,
  multiline: false,
};

// COMPONENT METADATA: Essential for debugging and development tools
DebouncedTextField.displayName = 'DebouncedTextField';

export default DebouncedTextField;

// Display name for React DevTools debugging
DebouncedTextField.displayName = 'ERA² DebouncedTextField';

// REACT VERSION COMPATIBILITY NOTES:
// ==================================
//
// CURRENT APPROACH (React 18 Style):
// - Uses React.forwardRef for explicit ref forwarding
// - Compatible with React 16.3+ through React 18
// - Clear, debuggable ref handling
// - Consistent with existing codebase patterns
//
// FUTURE MIGRATION (React 19):
// When migrating to React 19, this component can be simplified to:
//
// const DebouncedTextField = ({ ref, maxLength = 0, debounceDelay = 500, ... }) => {
//   // Same implementation, but ref is just another prop
//   return <TextField ref={ref} ... />;
// };
//
// MIGRATION CHECKLIST:
// 1. Ensure all consuming components work with React 19
// 2. Remove React.forwardRef wrapper
// 3. Add ref to destructured props
// 4. Update PropTypes to include ref
// 5. Test ref forwarding functionality
// 6. Update documentation

// PERFORMANCE NOTES:
// ==================
// 1. DEBOUNCE DELAYS:
//    - 300ms: Good for general text input (names, addresses)
//    - 500ms: Better for calculation-triggering fields (values, quantities)
//    - 0ms: Use for dates, dropdowns, or fields requiring immediate validation
//
// 2. MEMORY OPTIMIZATION:
//    - useCallback prevents handleInputChange recreation on every render
//    - Timeout cleanup prevents memory leaks
//    - Synthetic event creation is lightweight and only done when needed
//
// 3. RENDER OPTIMIZATION:
//    - Local state prevents parent re-renders on every keystroke
//    - Only triggers parent onChange after user stops typing
//    - Maintains smooth typing experience with immediate visual feedback
//
// 4. REF FORWARDING BENEFITS:
//    - Enables programmatic focus management for form validation
//    - Supports integration with form libraries (React Hook Form, Formik)
//    - Allows measurement and DOM manipulation when needed
//    - Maintains accessibility features like screen reader navigation
//
// 5. COMPATIBILITY:
//    - Maintains exact same API as original TextField
//    - Synthetic events match native event structure
//    - Can be used as drop-in replacement for existing TextField usage
//    - Forward-compatible with React 19 migration path
